import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AssistantTodoItem,
  GetAllAssistantTodosResponse,
  SubThreadCreatedEvent,
  SubThreadUpdateEvent,
  SubThreadStatus,
  AgentStreamEvents,
  ResponseArtifactDTO,
  ResponseArtifactCreatedEvent,
  ResponseArtifactUpdatedEvent,
  GetUserArtifactsResponse,
  UpdateResponseArtifactRequest,
  UpdateResponseArtifactResponse,
} from '@dreamai/types';
import { useAgent } from '../../contexts/AgentContext';
import { usePusher } from '../../contexts/PusherContext';
import {
  useResponseArtifactEvents,
  useOnboardingModals,
} from '../../providers';
import api from '../../api';
import DismissTodoModal from './sidebar/DismissTodoModal';
import ResponseArtifactModal from './sidebar/ResponseArtifactModal';
import { isOrganisationChargeError } from './sidebar/organisationSpend';
import { isHotelQuoteExpiredError } from './sidebar/hotelArtifact';
import TasksSkeleton from './sidebar/TasksSkeleton';
import { ReactComponent as SnoozeIcon } from '../../icons/snooze.svg';
import { ReactComponent as TrashIcon } from '../../icons/trash-bin.svg';
import { ReactComponent as CrossIcon } from '../../icons/cross.svg';
import { ReactComponent as GridIcon } from '../../icons/grid-01.svg';
import { ReactComponent as MessageQuestionIcon } from '../../icons/message-question-circle.svg';
import { ReactComponent as ClipboardCheckIcon } from '../../icons/clipboard-check-outline.svg';
import { isRepresentedInWaitingForYou } from './activityHelpers';
import './activity-page.scss';

// ─── Helpers ──────────────────────────────────────────────────

type InFlightTab = 'chat' | 'projects';

const resolveDisplayStatus = (
  todo: AssistantTodoItem,
): 'complete' | 'failed' | 'processing' | 'waiting' => {
  // Thread-level status takes priority for terminal states
  if (todo.status === 'failed' || todo.status === 'cancelled_by_user')
    return 'failed';
  if (todo.status === 'complete') return 'complete';
  if (
    todo.status === 'waiting_on_user' ||
    todo.status === 'waiting_on_third_party'
  )
    return 'waiting';

  const steps = todo.steps || [];
  const activeStep =
    steps.find((s) => s.active) ||
    steps.find((s) => s.status === 'in_progress') ||
    steps.find((s) => s.status === 'waiting_on_user') ||
    steps.find((s) => s.status === 'waiting_on_third_party') ||
    steps.find((s) => s.status === 'failed') ||
    steps.find((s) => s.status === 'pending');
  const stepStatus = activeStep?.status;
  if (stepStatus === 'complete') return 'complete';
  if (stepStatus === 'failed') return 'failed';
  if (
    stepStatus === 'waiting_on_user' ||
    stepStatus === 'waiting_on_third_party'
  )
    return 'waiting';
  return 'processing';
};

/**
 * Extract the ETA timestamp from a completionEta string and return a
 * countdown label (e.g. "45m", "1h30", "2h"). Only renders when the ETA
 * is sensible:
 * - must use the `{timestamp}ISO{/timestamp}` tagged format
 * - the ISO timestamp must parse to a valid date
 * - the target must be in the future
 * - the remaining time must be under 24 hours
 * Returns null otherwise, in which case nothing is shown.
 */
const getEtaRemainingLabel = (
  eta: string | undefined,
  now: number,
): string | null => {
  if (!eta) return null;
  const tagMatch = eta.match(/\{timestamp\}([^{]+)\{\/timestamp\}/);
  if (!tagMatch) return null;
  const target = new Date(tagMatch[1].trim()).getTime();
  if (isNaN(target)) return null;
  const diffMinutes = Math.ceil((target - now) / 60000);
  if (diffMinutes <= 0) return null;
  if (diffMinutes >= 24 * 60) return null;
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes > 0
    ? `${hours}h${String(minutes).padStart(2, '0')}`
    : `${hours}h`;
};

const categoriseTodo = (todo: AssistantTodoItem): InFlightTab => {
  if (todo.parentThreadIsMainThread || todo.parentThreadIsWhatsappThread) {
    return 'chat';
  }
  return 'projects';
};

const getActionLabel = (artifact: ResponseArtifactDTO): string => {
  const type = artifact.artifactType as string;
  if (type === 'multiple_choice') return 'Make a choice';
  if (type === 'form_input')
    return artifact.confirmationId ? 'Review' : 'Review';
  if (type === 'spending_approval') return 'Review';
  if (type === 'yes_or_no') return 'Make a choice';
  if (type === 'confirmation_query') return 'Review';
  if (type === 'expense_review') return 'Review';
  return 'View options';
};

const getArtifactTypeIcon = (
  artifact: ResponseArtifactDTO,
): React.ReactNode => {
  const type = artifact.artifactType as string;
  if (type === 'multiple_choice') {
    return <GridIcon className='waiting-type-icon' />;
  }
  if (type === 'form_input' || type === 'yes_or_no') {
    return (
      <MessageQuestionIcon className='waiting-type-icon waiting-type-icon--lg' />
    );
  }
  if (
    type === 'spending_approval' ||
    type === 'confirmation_query' ||
    type === 'expense_review'
  ) {
    return <ClipboardCheckIcon className='waiting-type-icon' />;
  }
  return <ClipboardCheckIcon className='waiting-type-icon' />;
};

const getArtifactDescription = (artifact: ResponseArtifactDTO): string => {
  if (artifact.overview) return artifact.overview;
  if (artifact.introduction) return artifact.introduction;
  if (artifact.choices && artifact.choices.length > 0) {
    return `Found ${artifact.choices.length} options`;
  }
  if (artifact.fields && artifact.fields.length > 0) {
    return `${artifact.fields.length} fields to complete`;
  }
  return '';
};

// ─── Component ────────────────────────────────────────────────

interface ActivityPageProps {
  onActivityChanged?: () => void;
}

const ActivityPage: React.FC<ActivityPageProps> = ({ onActivityChanged }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { activeAgent } = useAgent();
  const { hasSeenModal } = useOnboardingModals();
  const pusherCtx = usePusher(true) as any;
  const pusherChannel = pusherCtx?.pusherChannel ?? null;

  // In-flight state
  const [todos, setTodos] = useState<AssistantTodoItem[]>([]);
  // Mirror of `todos` so Pusher handlers can read the latest list without being
  // re-bound on every change.
  const todosRef = useRef<AssistantTodoItem[]>([]);
  useEffect(() => {
    todosRef.current = todos;
  }, [todos]);
  const [todosLoading, setTodosLoading] = useState(true);
  const [inflightTab, setInflightTab] = useState<InFlightTab>('chat');

  // Waiting for you state
  const [pendingArtifacts, setPendingArtifacts] = useState<
    ResponseArtifactDTO[]
  >([]);
  const [artifactsLoading, setArtifactsLoading] = useState(true);
  const [waitingTab, setWaitingTab] = useState<'ready' | 'snoozed'>('ready');
  const [snoozeDropdownId, setSnoozeDropdownId] = useState<string | null>(null);

  // Stop task modal
  const [stopModalTodo, setStopModalTodo] = useState<AssistantTodoItem | null>(
    null,
  );

  // Minute ticker so ETA countdowns stay fresh
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);

  // Artifact modal
  const [artifactModalOpen, setArtifactModalOpen] = useState(false);
  const [fullArtifact, setFullArtifact] = useState<ResponseArtifactDTO | null>(
    null,
  );
  const [loadingArtifact, setLoadingArtifact] = useState(false);
  const modalArtifacts = useMemo(
    () => (fullArtifact ? [fullArtifact] : []),
    [fullArtifact],
  );

  // ─── Fetch In-flight ──────────────────────────────────────

  const fetchTodos = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetAllAssistantTodosResponse>('/todos', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTodos(res.data.todos);
    } catch (err) {
      console.error('ActivityPage: failed to load in-flight tasks', err);
    } finally {
      setTodosLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  // ─── Fetch Waiting-for-you ────────────────────────────────

  const fetchArtifacts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetUserArtifactsResponse>(
        '/response-artifacts/user/artifacts?status=pending',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Exclude blank_todos
      const filtered = (res.data.artifacts || []).filter(
        (a) => a.artifactType !== 'blank_todo',
      );
      setPendingArtifacts(filtered);
    } catch (err) {
      console.error('ActivityPage: failed to load artifacts', err);
    } finally {
      setArtifactsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchArtifacts();
  }, [fetchArtifacts]);

  // ─── Pusher: In-flight ────────────────────────────────────

  useEffect(() => {
    if (!pusherChannel) return;

    const handleCreated = (data: SubThreadCreatedEvent) => {
      if (!data.subThread) return;
      if (data.subThread.isInternalWork) return;
      if (data.notVisibleInTaskList) return;
      const newTodo: AssistantTodoItem = {
        id: data.subThread.id,
        description: data.subThread.description,
        status: data.subThread.status as SubThreadStatus,
        summary: data.subThread.summary,
        completionEta: data.subThread.completionEta,
        steps: data.subThread.steps,
        createdAt: data.subThread.createdAt,
        updatedAt: data.subThread.updatedAt,
        parentThreadId: data.parentThreadId,
        parentThreadDescription: data.parentThreadDescription || 'Thread',
        parentThreadIsMainThread: data.parentThreadIsMainThread,
        parentThreadIsInternalMonologue: data.parentThreadIsInternalMonologue,
        parentThreadIsResponseArtifactThread:
          data.parentThreadIsResponseArtifactThread,
        parentThreadIsRehomedTask: data.parentThreadIsRehomedTask,
        parentThreadIsWhatsappThread: data.parentThreadIsWhatsappThread,
        informationDumps: data.subThread.informationDumps,
        iconImageUrl: data.subThread.iconImageUrl,
        gmailLink: data.subThread.gmailLink,
        latestArtifact: (data.subThread as AssistantTodoItem).latestArtifact,
      };
      setTodos((prev) => {
        if (prev.some((t) => t.id === newTodo.id)) return prev;
        return [newTodo, ...prev];
      });
    };

    const handleUpdated = (data: SubThreadUpdateEvent) => {
      if (!data.subThread) return;
      if (data.subThread.isInternalWork) return;
      if (data.notVisibleInTaskList) return;

      // Task became visible (e.g. surfaced via setTaskVisibility) but isn't in
      // our list yet — refetch /todos so it appears live. We refetch rather than
      // build the row from the event because SUB_THREAD_UPDATE doesn't carry the
      // parent-thread context (e.g. parentThreadIsMainThread) the tab
      // categorisation needs; the /todos endpoint computes it server-side.
      if (!todosRef.current.some((t) => t.id === data.subThread.id)) {
        fetchTodos();
        return;
      }

      setTodos((prev) => {
        const idx = prev.findIndex((t) => t.id === data.subThread.id);
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = {
          ...prev[idx],
          status: data.subThread.status as SubThreadStatus,
          summary: data.subThread.summary,
          completionEta: data.subThread.completionEta,
          steps: data.subThread.steps,
          updatedAt: data.subThread.updatedAt,
          informationDumps: data.subThread.informationDumps,
          iconImageUrl: data.subThread.iconImageUrl ?? prev[idx].iconImageUrl,
          gmailLink: data.subThread.gmailLink ?? prev[idx].gmailLink,
          latestArtifact: (data.subThread as AssistantTodoItem).latestArtifact,
          isArchivedThread:
            (data.subThread as AssistantTodoItem).isArchivedThread ??
            prev[idx].isArchivedThread,
        };
        return updated;
      });
    };

    pusherChannel.bind(AgentStreamEvents.SUB_THREAD_CREATED, handleCreated);
    pusherChannel.bind(AgentStreamEvents.SUB_THREAD_UPDATE, handleUpdated);
    return () => {
      pusherChannel.unbind(AgentStreamEvents.SUB_THREAD_CREATED, handleCreated);
      pusherChannel.unbind(AgentStreamEvents.SUB_THREAD_UPDATE, handleUpdated);
    };
  }, [pusherChannel, fetchTodos]);

  // ─── Pusher: Artifacts ────────────────────────────────────

  const {
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  } = useResponseArtifactEvents();

  useEffect(() => {
    const cbId = 'activity-page';

    registerCreatedCallback(cbId, (data: ResponseArtifactCreatedEvent) => {
      if (!data.artifact) return;
      if (data.artifact.artifactType === 'information_dump') return;
      if ((data.artifact.artifactType as string) === 'suggestion') return;
      if (data.artifact.artifactType === 'blank_todo') return;
      const dto: ResponseArtifactDTO = {
        ...data.artifact,
        createdAt: data.artifact.createdAt || new Date().toISOString(),
        updatedAt: data.artifact.updatedAt || new Date().toISOString(),
      };
      setPendingArtifacts((prev) => {
        if (prev.some((a) => a.id === dto.id)) return prev;
        return [dto, ...prev];
      });
    });

    registerUpdatedCallback(cbId, (data: ResponseArtifactUpdatedEvent) => {
      if (!data.artifactId) return;
      if (data.status === 'actioned') {
        setPendingArtifacts((prev) =>
          prev.filter((a) => a.id !== data.artifactId),
        );
      }
    });

    return () => {
      unregisterCreatedCallback(cbId);
      unregisterUpdatedCallback(cbId);
    };
  }, [
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  ]);

  // ─── Categorised in-flight todos ──────────────────────────

  const activeTodos = useMemo(() => {
    return todos.filter(
      (t) => !t.isArchivedThread && !isRepresentedInWaitingForYou(t),
    );
  }, [todos]);

  const categorised = useMemo(() => {
    const chat: AssistantTodoItem[] = [];
    const projects: AssistantTodoItem[] = [];

    activeTodos.forEach((todo) => {
      const cat = categoriseTodo(todo);
      if (cat === 'chat') chat.push(todo);
      else projects.push(todo);
    });

    const sortByCreated = (a: AssistantTodoItem, b: AssistantTodoItem) => {
      const aComplete = resolveDisplayStatus(a) === 'complete';
      const bComplete = resolveDisplayStatus(b) === 'complete';
      if (aComplete !== bComplete) return aComplete ? 1 : -1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    };

    chat.sort(sortByCreated);
    projects.sort(sortByCreated);

    return { chat, projects };
  }, [activeTodos]);

  const currentInflightList = categorised[inflightTab];

  const tabCounts = useMemo(
    () => ({
      chat: categorised.chat.length,
      projects: categorised.projects.length,
    }),
    [categorised],
  );

  // ─── Artifact modal ───────────────────────────────────────

  const openArtifactForItem = useCallback(
    async (artifact: ResponseArtifactDTO) => {
      setLoadingArtifact(true);
      setArtifactModalOpen(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{
          success: boolean;
          artifact: ResponseArtifactDTO;
        }>(`/response-artifacts/artifact/${artifact.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) setFullArtifact(res.data.artifact);
      } catch (err) {
        console.error('Failed to fetch artifact:', err);
      } finally {
        setLoadingArtifact(false);
      }
    },
    [getAccessTokenSilently],
  );

  const closeArtifactModal = useCallback(() => {
    setArtifactModalOpen(false);
    setFullArtifact(null);
  }, []);

  const handleSubmitArtifactResponse = useCallback(
    async (
      artifact: ResponseArtifactDTO,
      response: any,
      mandateOptions?: any,
      cardSelection?: any,
    ) => {
      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: artifact.id,
          status: 'actioned',
          userResponse: response,
          source: 'todo_list',
          ...(mandateOptions?.createMandate && {
            create_mandate: true,
            mandate_items: mandateOptions.mandateItems,
          }),
          ...(cardSelection && { card_selection: cardSelection }),
        };
        await api.patch<UpdateResponseArtifactResponse>(
          `/response-artifacts/${artifact.id}`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        // Optimistic removal
        setPendingArtifacts((prev) => prev.filter((a) => a.id !== artifact.id));
        closeArtifactModal();
      } catch (err) {
        if (isOrganisationChargeError(err) || isHotelQuoteExpiredError(err))
          throw err;
        console.error('Error submitting artifact response:', err);
      }
    },
    [getAccessTokenSilently, closeArtifactModal],
  );

  // ─── Snooze / Trash handlers ─────────────────────────────

  const handleSnooze = useCallback(
    async (artifactId: string, minutes: number) => {
      try {
        const token = await getAccessTokenSilently();
        const snoozedUntil = new Date(
          Date.now() + minutes * 60 * 1000,
        ).toISOString();
        await api.patch(
          `/response-artifacts/${artifactId}/snooze`,
          { snoozedUntil },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setPendingArtifacts((prev) =>
          prev.map((a) => (a.id === artifactId ? { ...a, snoozedUntil } : a)),
        );
      } catch (err) {
        console.error('Failed to snooze artifact:', err);
      } finally {
        setSnoozeDropdownId(null);
      }
    },
    [getAccessTokenSilently],
  );

  const handleUnsnooze = useCallback(
    async (artifactId: string) => {
      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/response-artifacts/${artifactId}/snooze`,
          { snoozedUntil: null },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setPendingArtifacts((prev) =>
          prev.map((a) =>
            a.id === artifactId ? { ...a, snoozedUntil: undefined } : a,
          ),
        );
      } catch (err) {
        console.error('Failed to unsnooze artifact:', err);
      }
    },
    [getAccessTokenSilently],
  );

  const handleTrash = useCallback(
    async (artifact: ResponseArtifactDTO) => {
      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: artifact.id,
          status: 'actioned',
          userResponse: { acknowledged: true, dismissed: true },
          source: 'todo_list',
        };
        await api.patch(`/response-artifacts/${artifact.id}`, request, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPendingArtifacts((prev) => prev.filter((a) => a.id !== artifact.id));
      } catch (err) {
        console.error('Failed to trash artifact:', err);
      }
    },
    [getAccessTokenSilently],
  );

  // ─── Filter snoozed vs ready artifacts ──────────────────

  const readyArtifacts = useMemo(
    () =>
      pendingArtifacts.filter(
        (a) => !a.snoozedUntil || new Date(a.snoozedUntil) <= new Date(),
      ),
    [pendingArtifacts],
  );

  const snoozedArtifacts = useMemo(
    () =>
      pendingArtifacts.filter(
        (a) => a.snoozedUntil && new Date(a.snoozedUntil) > new Date(),
      ),
    [pendingArtifacts],
  );

  // ─── Load more state ─────────────────────────────────────

  const COLLAPSED_LIMIT = 3;
  const [inflightExpanded, setInflightExpanded] = useState(false);
  const [waitingExpanded, setWaitingExpanded] = useState(false);

  const visibleInflightList = useMemo(() => {
    if (inflightExpanded || currentInflightList.length <= COLLAPSED_LIMIT) {
      return currentInflightList;
    }
    return currentInflightList.slice(0, COLLAPSED_LIMIT);
  }, [currentInflightList, inflightExpanded]);

  const hasMoreInflight =
    currentInflightList.length > COLLAPSED_LIMIT && !inflightExpanded;

  const visibleWaitingList = useMemo(() => {
    if (waitingExpanded || readyArtifacts.length <= COLLAPSED_LIMIT) {
      return readyArtifacts;
    }
    return readyArtifacts.slice(0, COLLAPSED_LIMIT);
  }, [readyArtifacts, waitingExpanded]);

  const hasMoreWaiting =
    readyArtifacts.length > COLLAPSED_LIMIT && !waitingExpanded;

  // Reset expanded state when switching tabs
  useEffect(() => {
    setInflightExpanded(false);
  }, [inflightTab]);

  // ─── Handlers ─────────────────────────────────────────────

  const handleToggleWhatsappUpdates = useCallback(
    async (subThreadId: string, enabled: boolean) => {
      // Optimistic update
      setTodos((prev) =>
        prev.map((t) =>
          t.id === subThreadId ? { ...t, whatsappUpdatesEnabled: enabled } : t,
        ),
      );
      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/todos/${subThreadId}/whatsapp-updates`,
          { enabled },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Failed to toggle WhatsApp updates:', err);
        // Revert on failure
        setTodos((prev) =>
          prev.map((t) =>
            t.id === subThreadId
              ? { ...t, whatsappUpdatesEnabled: !enabled }
              : t,
          ),
        );
      }
    },
    [getAccessTokenSilently],
  );

  const handleDismissTask = useCallback(
    async (todo: AssistantTodoItem) => {
      const isTerminal =
        todo.status === 'complete' ||
        todo.status === 'failed' ||
        todo.status === 'cancelled_by_user';

      // Optimistic removal
      setTodos((prev) => prev.filter((t) => t.id !== todo.id));

      try {
        const token = await getAccessTokenSilently();
        if (!isTerminal) {
          // Stop the task first
          await api.post(
            `/todos/${todo.id}/cancel`,
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }
        // Then archive/dismiss
        await api.post(
          `/todos/${todo.id}/archive`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        onActivityChanged?.();
      } catch (err) {
        console.error('Error dismissing task:', err);
        // Revert on failure
        setTodos((prev) => [...prev, todo]);
      }
    },
    [getAccessTokenSilently, onActivityChanged],
  );

  const handleDismissClick = useCallback(
    (todo: AssistantTodoItem) => {
      const isTerminal =
        todo.status === 'complete' ||
        todo.status === 'failed' ||
        todo.status === 'cancelled_by_user';

      // Only show warning if we're actually stopping a running task
      if (!isTerminal && !hasSeenModal('dismiss_todo_warning')) {
        setStopModalTodo(todo);
      } else {
        handleDismissTask(todo);
      }
    },
    [hasSeenModal, handleDismissTask],
  );

  // ─── Render helpers ───────────────────────────────────────

  const agentImageUrl = activeAgent?.image_url;

  const renderInflightItem = (todo: AssistantTodoItem) => {
    const displayStatus = resolveDisplayStatus(todo);
    const isComplete =
      displayStatus === 'complete' || displayStatus === 'failed';
    const isWaiting = displayStatus === 'waiting';
    const stepText = isComplete
      ? todo.summary || 'Done'
      : isWaiting
        ? todo.summary || 'Waiting for you'
        : todo.steps?.find((s) => s.active)?.description ||
          todo.summary ||
          'Working on it...';
    const etaLabel = getEtaRemainingLabel(todo.completionEta, now);

    return (
      <div key={todo.id} className={`activity-task-item ${displayStatus}`}>
        <div className='task-avatar-wrapper'>
          {isComplete ? (
            displayStatus === 'failed' ? (
              <>
                <svg
                  className='avatar-ring'
                  xmlns='http://www.w3.org/2000/svg'
                  width='42'
                  height='42'
                  viewBox='0 0 38 38'
                  fill='none'
                >
                  <circle
                    cx='19'
                    cy='19'
                    r='18'
                    stroke='#FF5300'
                    strokeWidth='2'
                  />
                </svg>
                <svg
                  className='task-status-icon'
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M12 8V12M12 16H12.01M7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21Z'
                    stroke='#FF5300'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </>
            ) : (
              <>
                <svg
                  className='avatar-ring'
                  xmlns='http://www.w3.org/2000/svg'
                  width='42'
                  height='42'
                  viewBox='0 0 38 38'
                  fill='none'
                >
                  <circle
                    cx='19'
                    cy='19'
                    r='18'
                    stroke='#2298DD'
                    strokeWidth='2'
                  />
                </svg>
                <svg
                  className='task-status-icon'
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M20 6L9 17L4 12'
                    stroke='#2298DD'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </>
            )
          ) : isWaiting ? (
            <>
              <svg
                className='avatar-ring'
                xmlns='http://www.w3.org/2000/svg'
                width='42'
                height='42'
                viewBox='0 0 38 38'
                fill='none'
              >
                <circle
                  opacity='0.4'
                  cx='19'
                  cy='19'
                  r='18'
                  stroke='#2298DD'
                  strokeWidth='2'
                />
              </svg>
              <div className='task-avatar-inner'>
                {agentImageUrl ? (
                  <img className='task-avatar' src={agentImageUrl} alt='' />
                ) : (
                  <div className='task-avatar placeholder' />
                )}
              </div>
            </>
          ) : (
            <>
              <svg
                className='avatar-spinner'
                xmlns='http://www.w3.org/2000/svg'
                width='42'
                height='42'
                viewBox='0 0 38 38'
                fill='none'
              >
                <circle
                  opacity='0.3'
                  cx='19'
                  cy='19'
                  r='18'
                  stroke='#2298DD'
                  strokeWidth='2'
                />
                <path
                  d='M1 19C1 9.05887 9.05887 1 19 1'
                  stroke='#2298DD'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </svg>
              <div className='task-avatar-inner task-avatar-inner--eta'>
                {etaLabel && <span className='task-eta-text'>{etaLabel}</span>}
              </div>
            </>
          )}
        </div>
        <div className='task-text'>
          <span className='task-description'>{todo.description}</span>
          <span
            className={`task-summary${isComplete ? ` ${displayStatus}` : ''}`}
          >
            {stepText}
          </span>
        </div>
        <div className='task-actions-wrapper'>
          {isComplete && (
            <span
              className={`completed-text${displayStatus === 'failed' ? ' failed' : ''}`}
            >
              {displayStatus === 'failed' ? 'Failed' : 'Completed'}
            </span>
          )}
          {isWaiting && (
            <span className='waiting-for-you-text'>
              {todo.status === 'waiting_on_third_party'
                ? 'Waiting on third party'
                : 'Waiting for you'}
            </span>
          )}
          <button
            className='dismiss-task-btn'
            title='Dismiss'
            onClick={(e) => {
              e.stopPropagation();
              handleDismissClick(todo);
            }}
          >
            <CrossIcon />
          </button>
        </div>
      </div>
    );
  };

  const renderWaitingItem = (artifact: ResponseArtifactDTO) => {
    const title = artifact.todoDescription || artifact.title || 'Pending task';
    const actionLabel = getActionLabel(artifact);
    const description = getArtifactDescription(artifact);

    return (
      <div key={artifact.id} className='activity-waiting-item'>
        <div
          className='waiting-type-badge waiting-clickable'
          onClick={() => openArtifactForItem(artifact)}
        >
          {getArtifactTypeIcon(artifact)}
        </div>
        <div
          className='waiting-text waiting-clickable'
          onClick={() => openArtifactForItem(artifact)}
        >
          <span className='waiting-title'>{title}</span>
          {description && (
            <span className='waiting-description'>{description}</span>
          )}
        </div>
        <div className='waiting-actions'>
          <button
            className='waiting-action-btn'
            onClick={() => openArtifactForItem(artifact)}
          >
            {actionLabel}
          </button>
          <div className='snooze-wrapper'>
            <button
              className='waiting-icon-btn'
              title='Snooze'
              onClick={(e) => {
                e.stopPropagation();
                setSnoozeDropdownId(
                  snoozeDropdownId === artifact.id ? null : artifact.id,
                );
              }}
            >
              <SnoozeIcon />
            </button>
            {snoozeDropdownId === artifact.id && (
              <div className='snooze-dropdown'>
                <button onClick={() => handleSnooze(artifact.id, 30)}>
                  30 minutes
                </button>
                <button onClick={() => handleSnooze(artifact.id, 360)}>
                  6 hours
                </button>
                <button onClick={() => handleSnooze(artifact.id, 1440)}>
                  1 day
                </button>
                <button onClick={() => handleSnooze(artifact.id, 10080)}>
                  1 week
                </button>
              </div>
            )}
          </div>
          <button
            className='waiting-icon-btn'
            title='Delete'
            onClick={(e) => {
              e.stopPropagation();
              handleTrash(artifact);
            }}
          >
            <TrashIcon />
          </button>
        </div>
      </div>
    );
  };

  // ─── Main render ──────────────────────────────────────────

  return (
    <div className='activity-page'>
      <div className='activity-header'>Activity</div>

      {/* In-flight section */}
      <section className='activity-section'>
        <div className='section-header'>
          <span className='section-header-text'>In-flight</span>
          {activeTodos.length > 0 && (
            <span className='section-header-count inflight'>
              {activeTodos.length}
            </span>
          )}
        </div>

        <div className='inflight-tabs'>
          <button
            className={`inflight-tab${inflightTab === 'chat' ? ' active' : ''}`}
            onClick={() => setInflightTab('chat')}
          >
            <span className='tab-label'>Chat & WhatsApp</span>
            {tabCounts.chat > 0 && (
              <span className='tab-count'>{tabCounts.chat}</span>
            )}
          </button>
          <button
            className={`inflight-tab${inflightTab === 'projects' ? ' active' : ''}`}
            onClick={() => setInflightTab('projects')}
          >
            <span className='tab-label'>Projects</span>
            {tabCounts.projects > 0 && (
              <span className='tab-count'>{tabCounts.projects}</span>
            )}
          </button>
        </div>

        {todosLoading ? (
          <TasksSkeleton />
        ) : currentInflightList.length === 0 ? (
          <div className='empty-state-inflight'>
            <img
              className='empty-state-image'
              src='/splicons/no-tasks-clipboard.png'
              alt=''
            />
            <div className='empty-state-text'>
              <span className='empty-state-title'>
                I'm not working on anything right now
              </span>
              <span className='empty-state-subtitle'>
                You can find activity from your Tasks, Projects, and Routines
                here
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className='inflight-list'>
              {visibleInflightList.map(renderInflightItem)}
            </div>
            {hasMoreInflight && (
              <div className='load-more-wrapper'>
                <button
                  className='load-more-btn'
                  onClick={() => setInflightExpanded(true)}
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Waiting for you section */}
      <section className='activity-section'>
        <div className='section-header'>
          <span className='section-header-text'>Waiting for You</span>
          {readyArtifacts.length > 0 && (
            <span className='section-header-count inflight'>
              {readyArtifacts.length}
            </span>
          )}
        </div>

        <div className='inflight-tabs'>
          <button
            className={`inflight-tab${waitingTab === 'ready' ? ' active' : ''}`}
            onClick={() => setWaitingTab('ready')}
          >
            <span className='tab-label'>Ready</span>
            {readyArtifacts.length > 0 && (
              <span className='tab-count'>{readyArtifacts.length}</span>
            )}
          </button>
          <button
            className={`inflight-tab${waitingTab === 'snoozed' ? ' active' : ''}`}
            onClick={() => setWaitingTab('snoozed')}
          >
            <span className='tab-label'>Snoozed</span>
            {snoozedArtifacts.length > 0 && (
              <span className='tab-count'>{snoozedArtifacts.length}</span>
            )}
          </button>
        </div>

        {waitingTab === 'ready' && (
          <>
            {artifactsLoading ? (
              <TasksSkeleton />
            ) : readyArtifacts.length === 0 ? (
              <div className='empty-state-inflight'>
                <img
                  className='empty-state-image'
                  src='/splicons/no-tasks-clipboard.png'
                  alt=''
                />
                <div className='empty-state-text'>
                  <span className='empty-state-title'>
                    Nothing waiting for you right now
                  </span>
                  <span className='empty-state-subtitle'>
                    Anything that needs your input will show up here
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className='waiting-list'>
                  {visibleWaitingList.map(renderWaitingItem)}
                </div>
                {hasMoreWaiting && (
                  <div className='load-more-wrapper'>
                    <button
                      className='load-more-btn'
                      onClick={() => setWaitingExpanded(true)}
                    >
                      Load more
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {waitingTab === 'snoozed' && (
          <>
            {snoozedArtifacts.length === 0 ? (
              <div className='empty-state snoozed-empty'>
                <img
                  src='/images/empty-snoozed.png'
                  alt=''
                  width={72}
                  height={72}
                />
                <h3>You've not snoozed any tasks</h3>
                <p>Pause notifications about a task, snooze it for 24 hours.</p>
              </div>
            ) : (
              <div className='waiting-list'>
                {snoozedArtifacts.map((artifact) => {
                  const title =
                    artifact.todoDescription ||
                    artifact.title ||
                    'Pending task';
                  const snoozedDate = artifact.snoozedUntil
                    ? new Date(artifact.snoozedUntil)
                    : null;
                  const snoozeLabel = snoozedDate
                    ? `Until ${snoozedDate.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                    : '';
                  return (
                    <div key={artifact.id} className='activity-waiting-item'>
                      <div className='waiting-type-badge'>
                        {getArtifactTypeIcon(artifact)}
                      </div>
                      <div className='waiting-text'>
                        <span className='waiting-title'>{title}</span>
                        <span className='waiting-description'>
                          {snoozeLabel}
                        </span>
                      </div>
                      <div className='waiting-actions'>
                        <button
                          className='waiting-action-btn'
                          onClick={() => handleUnsnooze(artifact.id)}
                        >
                          Unsnooze
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>

      {/* Artifact modal */}
      <DismissTodoModal
        show={!!stopModalTodo}
        todo={stopModalTodo}
        onClose={() => setStopModalTodo(null)}
        onConfirm={(todo) => {
          setStopModalTodo(null);
          handleDismissTask(todo);
        }}
      />

      <ResponseArtifactModal
        show={artifactModalOpen}
        artifacts={modalArtifacts}
        loading={loadingArtifact}
        onClose={closeArtifactModal}
        onSubmitResponse={handleSubmitArtifactResponse}
        onOptimisticAction={(artifact, action) => {
          if (action === 'submit' || action === 'dismiss') {
            setPendingArtifacts((prev) =>
              prev.filter((a) => a.id !== artifact.id),
            );
            closeArtifactModal();
          }
        }}
      />
    </div>
  );
};

export default ActivityPage;
